import { useState, useEffect } from 'react';
import { publicApiCall } from '../services/api';

/**
 * Branding (custom logo) shared across the app.
 *
 * The logo is served publicly through CloudFront at a stable same-domain URL
 * (https://<domain>/branding/logo.png). This hook fetches that URL from the
 * public /auth/branding endpoint so both the unauthenticated login screen and
 * the authenticated dashboard can render it.
 *
 * A module-level cache + in-flight promise means the endpoint is hit at most
 * once per page load no matter how many components call the hook. refreshBranding()
 * clears it so the admin branding panel can force a re-fetch right after upload.
 */
export interface Branding {
  logoUrl: string;
  logoConfigured: boolean;
}

// Default logo path — same-origin CloudFront route. Works even before any custom
// logo is uploaded (a default logo.png is served) and if the API call fails.
const DEFAULT_BRANDING: Branding = {
  logoUrl: '/branding/logo.png',
  logoConfigured: false,
};

let cachedBranding: Branding | null = null;
let inFlight: Promise<Branding> | null = null;

async function fetchBranding(): Promise<Branding> {
  if (cachedBranding) return cachedBranding;
  if (inFlight) return inFlight;

  inFlight = publicApiCall('/auth/branding')
    .then((data: Partial<Branding>) => {
      const branding: Branding = {
        logoUrl: data?.logoUrl || DEFAULT_BRANDING.logoUrl,
        logoConfigured: Boolean(data?.logoConfigured),
      };
      cachedBranding = branding;
      return branding;
    })
    .catch((error) => {
      console.error('Failed to load branding, using default:', error);
      return DEFAULT_BRANDING;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Clears the branding cache so the next hook consumer re-fetches. Call this after
 * an admin uploads a new logo so the UI reflects the change without a reload.
 */
export function refreshBranding() {
  cachedBranding = null;
}

export function useBranding() {
  const [branding, setBranding] = useState<Branding>(cachedBranding || DEFAULT_BRANDING);
  const [loading, setLoading] = useState<boolean>(!cachedBranding);

  useEffect(() => {
    let active = true;

    fetchBranding()
      .then((result) => {
        if (active) {
          setBranding(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  return { branding, loading };
}
